import {
  FaBookOpen,
  FaCameraRetro,
  FaClock,
  FaEnvelopeOpenText,
  FaGift,
  FaHeart,
  FaImage,
  FaLayerGroup,
  FaMapMarkedAlt,
  FaMicrophoneAlt,
  FaMusic,
  FaRegStar,
  FaUniversity,
} from 'react-icons/fa';

export const GLOBAL_REVEAL_STYLE_OPTIONS = [
  'soft_fade',
  'ribbon_unwrap',
  'spotlight_lift',
  'card_peek',
  'cinematic_slide',
];

export const MODULE_REVEAL_META = {
  primary_media: {
    tone: 'photo',
    texture: 'none',
    defaultRevealStyle: 'photo_stack',
    revealStyles: ['photo_stack', 'film_strip', 'gallery_cards'],
    Icon: FaImage,
    kicker: 'Photo pull',
    title: 'Open the Photo Story',
    copy: 'A cover memory slides forward first, so the recipient starts with one clear image or video.',
    action: 'Pull Photo',
  },
  text: {
    tone: 'letter',
    texture: 'paper',
    defaultRevealStyle: 'envelope_flap',
    revealStyles: ['envelope_flap', 'paper_rise', 'handwritten_card'],
    Icon: FaEnvelopeOpenText,
    kicker: 'Letter unfold',
    title: 'Open the Letter',
    copy: 'The message opens like a private note, then the full letter appears without showing everything too soon.',
    action: 'Unfold Letter',
  },
  surprise_box: {
    tone: 'envelope',
    texture: 'paper',
    defaultRevealStyle: 'envelope_flap',
    revealStyles: ['envelope_flap', 'paper_rise', 'handwritten_card'],
    Icon: FaGift,
    kicker: 'Hidden note',
    title: 'Open When',
    copy: 'A small sealed moment opens only when they choose to touch it.',
    action: 'Open Note',
  },
  voice_postcard: {
    tone: 'voice',
    texture: 'none',
    defaultRevealStyle: 'wave_capsule',
    revealStyles: ['wave_capsule', 'postcard_player'],
    Icon: FaMicrophoneAlt,
    kicker: 'Voice capsule',
    title: 'Play the Voice Layer',
    copy: 'The soundwave wakes up first, then the voice postcard becomes the focus.',
    action: 'Wake Voice',
  },
  timeline: {
    tone: 'timeline',
    texture: 'paper',
    defaultRevealStyle: 'vertical_ribbon',
    revealStyles: ['vertical_ribbon', 'film_timeline'],
    Icon: FaClock,
    kicker: 'Time ribbon',
    title: 'Unroll the Timeline',
    copy: 'Dates and milestones arrive as a gentle path instead of a dense list.',
    action: 'Unroll Time',
  },
  guestbook: {
    tone: 'blessing',
    texture: 'paper',
    defaultRevealStyle: 'note_cards',
    revealStyles: ['note_cards', 'guest_card', 'sticky_notes'],
    Icon: FaHeart,
    kicker: 'Blessing wall',
    title: 'Open the Blessings',
    copy: 'Guest messages appear like small cards gathered around the gift.',
    action: 'Show Blessings',
  },
  keepsake_poster: {
    tone: 'keepsake',
    texture: 'paper',
    defaultRevealStyle: 'foil_card',
    revealStyles: ['foil_card', 'final_card'],
    Icon: FaRegStar,
    kicker: 'Final card',
    title: 'Reveal the Keepsake',
    copy: 'The ending opens as a saveable card, giving the gift a polished final moment.',
    action: 'Reveal Card',
  },
  song_dedication: {
    tone: 'music',
    texture: 'none',
    defaultRevealStyle: 'record_card',
    revealStyles: ['record_card', 'music_card'],
    Icon: FaMusic,
    kicker: 'Music moment',
    title: 'Open the Song',
    copy: 'A quiet music card enters before the dedication plays.',
    action: 'Open Song',
  },
  then_now: {
    tone: 'compare',
    texture: 'none',
    defaultRevealStyle: 'split_slide',
    revealStyles: ['split_slide', 'photo_stack'],
    Icon: FaLayerGroup,
    kicker: 'Before / After',
    title: 'Slide Then & Now',
    copy: 'Two memories meet in one swipeable reveal, made for comparison moments.',
    action: 'Slide Open',
  },
  frame_gallery: {
    tone: 'gallery',
    texture: 'none',
    defaultRevealStyle: 'gallery_cards',
    revealStyles: ['gallery_cards', 'photo_stack', 'film_strip'],
    Icon: FaImage,
    kicker: 'Framed memory',
    title: 'Lift the Frame',
    copy: 'A framed image opens with space for one beautiful, focused keepsake.',
    action: 'Lift Frame',
  },
  memory_map: {
    tone: 'map',
    texture: 'map',
    defaultRevealStyle: 'folded_map',
    revealStyles: ['folded_map', 'campus_map', 'venue_route'],
    Icon: FaMapMarkedAlt,
    kicker: 'Memory map',
    title: 'Unfold the Map',
    copy: 'Places and moments unfold like a small paper map made from the story.',
    action: 'Unfold Map',
  },
  tiny_museum: {
    tone: 'museum',
    texture: 'archive',
    defaultRevealStyle: 'glass_case',
    revealStyles: ['glass_case', 'archive_shelf'],
    Icon: FaUniversity,
    kicker: 'Tiny museum',
    title: 'Open the Exhibit',
    copy: 'A private exhibit opens for small objects, details, dates, or cherished fragments.',
    action: 'Open Exhibit',
  },
  polaroid_booth: {
    tone: 'polaroid',
    texture: 'paper',
    defaultRevealStyle: 'camera_print',
    revealStyles: ['camera_print', 'snapshot_stack'],
    Icon: FaCameraRetro,
    kicker: 'Polaroid booth',
    title: 'Take the Snapshot',
    copy: 'The module enters like a printed photo, playful but still clean on mobile.',
    action: 'Print Photo',
  },
  ai_generator: {
    tone: 'studio',
    texture: 'paper',
    defaultRevealStyle: 'paper_studio',
    revealStyles: ['paper_studio', 'light_studio'],
    Icon: FaBookOpen,
    kicker: 'AI studio',
    title: 'Open Writing Studio',
    copy: 'A private writing assistant layer for drafting or refining gift copy.',
    action: 'Open Studio',
  },
};

export const DEFAULT_REVEAL_META = {
  tone: 'gift',
  texture: 'none',
  defaultRevealStyle: 'gift_layer',
  revealStyles: ['gift_layer'],
  Icon: FaGift,
  kicker: 'Gift layer',
  title: 'Open This Layer',
  copy: 'This chapter opens as its own small moment before the next part of the gift appears.',
  action: 'Open Layer',
};

export const getRevealRegistryMeta = (moduleType) => MODULE_REVEAL_META[moduleType] || DEFAULT_REVEAL_META;

export const normalizeRevealStyle = (value = '') => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

export const resolveRevealStyle = (baseMeta = DEFAULT_REVEAL_META, requestedStyle = '') => {
  const normalizedStyle = normalizeRevealStyle(requestedStyle);
  const supportedStyles = Array.isArray(baseMeta.revealStyles) && baseMeta.revealStyles.length ?
    [...baseMeta.revealStyles, ...GLOBAL_REVEAL_STYLE_OPTIONS] :
    DEFAULT_REVEAL_META.revealStyles;

  if (normalizedStyle && supportedStyles.includes(normalizedStyle)) return normalizedStyle;
  return baseMeta.defaultRevealStyle || supportedStyles[0] || DEFAULT_REVEAL_META.defaultRevealStyle;
};
